import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Award, CheckCircle, Clock, XCircle, Upload, AlertTriangle, Calendar, FileText, Car, Wine
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const CERTIFICATION_ICONS = {
  ca_abc_rbs: Wine,
  alcohol_service: Award,
  food_handler: FileText,
  drivers_license: Car
};

const CertificationsPage = () => {
  const navigate = useNavigate();
  const [certTypes, setCertTypes] = useState({});
  const [myCerts, setMyCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    certification_type: '',
    document_url: '',
    expiration_date: '',
    credential_number: ''
  });
  const [rbsStatus, setRbsStatus] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [typesRes, myRes] = await Promise.all([
        api.get('/certifications/types'),
        api.get('/certifications/my-certifications')
      ]);
      setCertTypes(typesRes.data.certification_types || {});
      setMyCerts(myRes.data.certifications || []);
      try {
        const rbsRes = await api.get('/certifications/rbs/status');
        setRbsStatus(rbsRes.data);
      } catch { /* non-fatal */ }
    } catch (error) {
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }

    try {
      const sigRes = await api.post('/cloudinary/signature', {
        folder: 'certifications',
        resource_type: 'image'
      });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', 'certifications');

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      const data = await uploadRes.json();

      if (!data.secure_url) throw new Error('Upload failed');

      setUploadData(prev => ({ ...prev, document_url: data.secure_url }));
      toast.success('Document uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (!uploadData.certification_type || !uploadData.document_url || !uploadData.expiration_date) {
      toast.error('Please fill all required fields');
      return;
    }
    const cfg = certTypes[uploadData.certification_type] || {};
    if (cfg.requires_credential_id && !uploadData.credential_number.trim()) {
      toast.error(`${cfg.credential_id_label || 'Credential ID'} is required`);
      return;
    }

    setUploading(true);
    try {
      await api.post('/certifications/upload', uploadData);
      toast.success('Certification uploaded for review!');
      setUploadOpen(false);
      setUploadData({ certification_type: '', document_url: '', expiration_date: '', credential_number: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload certification');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (cert) => {
    if (cert.is_expired) {
      return <Badge className="bg-red-100 text-red-700">Expired</Badge>;
    }
    if (cert.is_expiring_soon) {
      return <Badge className="bg-amber-100 text-amber-700">Expiring Soon</Badge>;
    }
    switch (cert.status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-700">Pending Review</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge variant="outline">{cert.status}</Badge>;
    }
  };

  const getStatusIcon = (cert) => {
    if (cert.is_expired || cert.status === 'rejected') {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }
    if (cert.status === 'approved' && !cert.is_expiring_soon) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    if (cert.status === 'pending') {
      return <Clock className="w-5 h-5 text-blue-500" />;
    }
    return <AlertTriangle className="w-5 h-5 text-amber-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Group certifications by type. Hide the CA ABC RBS card for talent to
  // whom it doesn't apply (not a CA home state and no existing RBS cert).
  const rbsApplicable = !!(rbsStatus && rbsStatus.applicable);
  const certsByType = {};
  for (const [type, info] of Object.entries(certTypes)) {
    const existing = myCerts.find(c => c.certification_type === type);
    if (type === 'ca_abc_rbs' && !rbsApplicable && !existing) continue;
    certsByType[type] = { info, cert: existing };
  }
  // Cert types offered in the upload dropdown (same RBS gating).
  const uploadableTypes = Object.entries(certTypes).filter(
    ([type]) => type !== 'ca_abc_rbs' || rbsApplicable
      || myCerts.some(c => c.certification_type === 'ca_abc_rbs')
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="certifications-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <Button
            onClick={() => setUploadOpen(true)}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="upload-cert-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Certification
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-cyan-100 rounded-xl">
            <Award className="w-8 h-8 text-cyan-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Certifications & Licenses</h1>
            <p className="text-gray-500 mt-1">
              Upload your certifications to unlock more gig opportunities. Required certifications must be approved before you can apply to certain roles.
            </p>
          </div>
        </div>

        {/* California RBS status banner (CA talent / anyone holding RBS) */}
        {rbsStatus && rbsStatus.applicable && (
          <div
            data-testid="rbs-status-banner"
            className={`mb-6 rounded-xl border p-4 flex items-start gap-3 ${
              rbsStatus.status === 'certified' ? 'bg-green-50 border-green-200'
                : rbsStatus.status === 'expiring_soon' ? 'bg-amber-50 border-amber-200'
                : rbsStatus.status === 'expired' ? 'bg-red-50 border-red-200'
                : 'bg-blue-50 border-blue-200'}`}
          >
            <Wine className={`w-5 h-5 mt-0.5 shrink-0 ${
              rbsStatus.status === 'certified' ? 'text-green-600'
                : rbsStatus.status === 'expiring_soon' ? 'text-amber-600'
                : rbsStatus.status === 'expired' ? 'text-red-600' : 'text-blue-600'}`} />
            <div className="min-w-0">
              <p className="font-semibold text-gray-900">California ABC RBS: {rbsStatus.label}</p>
              <p className="text-sm text-gray-600 mt-0.5">
                {rbsStatus.status === 'not_certified'
                  ? 'California alcohol-service positions (bartending, beer/wine, cocktail & alcohol sampling) require a valid California ABC Responsible Beverage Service (RBS) certification. Add yours to stay eligible.'
                  : <>Server ID <strong>{rbsStatus.server_id || '—'}</strong>{rbsStatus.expiration_date ? <> · Expires {formatDateMonthDay(rbsStatus.expiration_date)}{typeof rbsStatus.days_until === 'number' ? ` (${rbsStatus.days_until} days)` : ''}</> : ''}</>}
              </p>
              {(rbsStatus.status === 'not_certified' || rbsStatus.status === 'expired' || rbsStatus.status === 'expiring_soon') && (
                <Button
                  size="sm"
                  className="mt-2 bg-cyan-500 hover:bg-cyan-600 text-white"
                  onClick={() => { setUploadData(prev => ({ ...prev, certification_type: 'ca_abc_rbs' })); setUploadOpen(true); }}
                  data-testid="rbs-update-btn"
                >
                  {rbsStatus.status === 'not_certified' ? 'Add RBS Certification' : 'Update RBS Certification'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Expiring Soon Alert */}
        {myCerts.some(c => c.is_expiring_soon && c.status === 'approved') && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              <strong>Attention:</strong> Some of your certifications are expiring soon. Please upload updated documents to maintain your eligibility.
            </AlertDescription>
          </Alert>
        )}

        {/* Certification Cards */}
        <div className="space-y-4">
          {Object.entries(certsByType).map(([type, { info, cert }]) => {
            const Icon = CERTIFICATION_ICONS[type] || FileText;
            return (
              <Card
                key={type}
                className="bg-white border-gray-200 hover:border-cyan-300 hover:shadow-sm transition cursor-pointer"
                data-testid={`cert-card-${type}`}
                onClick={(e) => {
                  if (e.target.closest('button,a,[role="button"]')) return;
                  setUploadData(prev => ({ ...prev, certification_type: type }));
                  setUploadOpen(true);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${cert?.status === 'approved' ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Icon className={`w-6 h-6 ${cert?.status === 'approved' ? 'text-green-600' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{info.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{info.description}</p>
                        {info.required_for_roles?.length > 0 && (
                          <p className="text-xs text-cyan-600 mt-2">
                            Required for: {info.required_for_roles.join(', ')}
                          </p>
                        )}
                        {cert && (
                          <div className="mt-3 space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              {getStatusIcon(cert)}
                              {getStatusBadge(cert)}
                            </div>
                            {cert.expiration_date && (
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Expires: {formatDateMonthDay(cert.expiration_date)}
                              </p>
                            )}
                            {cert.rejection_reason && (
                              <p className="text-xs text-red-600 mt-1">
                                Reason: {cert.rejection_reason}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      {!cert && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setUploadData(prev => ({ ...prev, certification_type: type }));
                            setUploadOpen(true);
                          }}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Upload
                        </Button>
                      )}
                      {cert?.status === 'rejected' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setUploadData(prev => ({ ...prev, certification_type: type }));
                            setUploadOpen(true);
                          }}
                        >
                          Re-upload
                        </Button>
                      )}
                      {cert?.is_expiring_soon && cert?.status === 'approved' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-amber-300 text-amber-700"
                          onClick={() => {
                            setUploadData(prev => ({ ...prev, certification_type: type }));
                            setUploadOpen(true);
                          }}
                        >
                          Renew
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Certification</DialogTitle>
            <DialogDescription>
              Upload your certification document for review. Please ensure the document is clear and valid.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Certification Type *</Label>
              <Select
                value={uploadData.certification_type}
                onValueChange={(v) => setUploadData(prev => ({ ...prev, certification_type: v }))}
              >
                <SelectTrigger data-testid="cert-type-select">
                  <SelectValue placeholder="Select certification type" />
                </SelectTrigger>
                <SelectContent>
                  {uploadableTypes.map(([type, info]) => (
                    <SelectItem key={type} value={type}>{info.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(certTypes[uploadData.certification_type] || {}).requires_credential_id && (
              <div className="space-y-2">
                <Label>{(certTypes[uploadData.certification_type] || {}).credential_id_label || 'Credential ID'} *</Label>
                <Input
                  type="text"
                  placeholder="Enter your RBS Server ID"
                  value={uploadData.credential_number}
                  onChange={(e) => setUploadData(prev => ({ ...prev, credential_number: e.target.value }))}
                  data-testid="cert-credential-input"
                />
                <p className="text-xs text-gray-500">Saved permanently to your certification profile for admin verification.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>
                Document Upload {(certTypes[uploadData.certification_type] || {}).proof_optional ? '(optional)' : '*'}
              </Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                data-testid="cert-file-input"
              />
              {uploadData.document_url && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Document ready
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Expiration Date *</Label>
              <Input
                type="date"
                value={uploadData.expiration_date}
                onChange={(e) => setUploadData(prev => ({ ...prev, expiration_date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                data-testid="cert-expiry-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={uploading || !uploadData.certification_type || !uploadData.expiration_date
                || (!(certTypes[uploadData.certification_type] || {}).proof_optional && !uploadData.document_url)
                || ((certTypes[uploadData.certification_type] || {}).requires_credential_id && !uploadData.credential_number.trim())}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="submit-cert-btn"
            >
              {uploading ? 'Uploading...' : 'Submit for Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificationsPage;
